import React, { useState, useEffect } from 'react';
import { X, Search, Loader2, UserPlus, Shuffle, Check, Users } from 'lucide-react';
import type { GroupeWithCounts, StudentBasic, TypeFormation } from '@/types/group.types';
import { getNiveauLabel, TypeFormationLabels } from '@/types/group.types';
import {
  getStudentsWithPendingAssignment,
  assignStudentsToGroup,
  assignStudentsRandomly,
  type FiliereForNavigation,
} from '@/services/groupsService';

interface Props {
  filiere: FiliereForNavigation;
  academicYearId: string;
  niveau: number;
  typeFormation: TypeFormation;
  groups: GroupeWithCounts[];
  campusId?: string;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const AssignStudentsModal: React.FC<Props> = ({
  filiere,
  academicYearId,
  niveau,
  typeFormation,
  groups,
  campusId,
  onClose,
  onSuccess,
  onError,
}) => {
  const [mode, setMode] = useState<'manual' | 'random'>('manual');
  const [students, setStudents] = useState<StudentBasic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [selectedGroupId, setSelectedGroupId] = useState<string>(groups[0]?.id || '');

  // Load pending students
  useEffect(() => {
    const loadStudents = async () => {
      setIsLoading(true);
      try {
        const data = await getStudentsWithPendingAssignment(
          filiere.id,
          niveau,
          typeFormation,
          academicYearId,
          campusId
        );
        setStudents(data);
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };

    loadStudents();
    // onError is a reporting callback, not a data dependency — re-running the
    // fetch whenever the parent re-renders would flash the list.
  }, [filiere.id, niveau, typeFormation, academicYearId, campusId]);

  // Filter students by search
  const filteredStudents = students.filter((s) => {
    const query = searchQuery.toLowerCase();
    return (
      s.firstName.toLowerCase().includes(query) ||
      s.lastName.toLowerCase().includes(query) ||
      s.studentCode.toLowerCase().includes(query)
    );
  });

  // Toggle student selection
  const toggleStudent = (studentId: string) => {
    setSelectedStudents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  // Select all filtered students
  const selectAll = () => {
    setSelectedStudents(new Set(filteredStudents.map((s) => s.id)));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedStudents(new Set());
  };

  // Handle manual assignment
  const handleManualAssign = async () => {
    if (selectedStudents.size === 0) {
      onError('Veuillez sélectionner au moins un étudiant');
      return;
    }

    if (!selectedGroupId) {
      onError('Veuillez sélectionner un groupe');
      return;
    }

    setIsSubmitting(true);
    try {
      await assignStudentsToGroup(selectedGroupId, {
        studentIds: Array.from(selectedStudents),
      });
      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de l\'assignation');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle random assignment
  const handleRandomAssign = async () => {
    setIsSubmitting(true);
    try {
      const result = await assignStudentsRandomly(
        filiere.id,
        niveau,
        typeFormation,
        academicYearId,
        campusId
      );
      if (result.assignedCount > 0) {
        onSuccess();
      } else {
        onError('Aucun étudiant à assigner');
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de l\'assignation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFC = typeFormation === 'CONTINUE';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Assigner des étudiants
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Info Bar */}
        <div className={`px-4 py-2 text-sm ${isFC ? 'bg-purple-50' : 'bg-primary-50'}`}>
          <span className={isFC ? 'text-purple-700' : 'text-primary-700'}>
            {filiere.filiere} • {getNiveauLabel(niveau)} • {TypeFormationLabels[typeFormation]}
          </span>
        </div>

        {/* Mode Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setMode('manual')}
            className={`flex-1 px-4 py-3 text-sm font-medium ${
              mode === 'manual'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <UserPlus className="h-4 w-4 inline mr-2" />
            Sélection manuelle
          </button>
          <button
            onClick={() => setMode('random')}
            className={`flex-1 px-4 py-3 text-sm font-medium ${
              mode === 'random'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Shuffle className="h-4 w-4 inline mr-2" />
            Distribution aléatoire
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Aucun étudiant en attente d'assignation pour ce niveau
            </div>
          ) : mode === 'manual' ? (
            <div className="space-y-4">
              {/* Group Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Groupe de destination
                </label>
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.studentCount}
                      {group.maxStudents && `/${group.maxStudents}`} étudiants)
                    </option>
                  ))}
                </select>
              </div>

              {/* Search and Selection Controls */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher un étudiant..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <button
                  onClick={selectAll}
                  className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Tout sélectionner
                </button>
                <button
                  onClick={clearSelection}
                  className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Désélectionner
                </button>
              </div>

              {/* Selection Count */}
              <div className="text-sm text-gray-500">
                {selectedStudents.size} étudiant(s) sélectionné(s) sur{' '}
                {filteredStudents.length}
              </div>

              {/* Student List */}
              <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                {filteredStudents.map((student) => (
                  <label
                    key={student.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                      selectedStudents.has(student.id) ? 'bg-primary-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.has(student.id)}
                      onChange={() => toggleStudent(student.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {student.lastName} {student.firstName}
                      </p>
                      <p className="text-xs text-gray-500">{student.studentCode}</p>
                    </div>
                    {selectedStudents.has(student.id) && (
                      <Check className="h-4 w-4 text-primary-600" />
                    )}
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Random Assignment Info */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="text-sm font-medium text-amber-800 mb-2">
                  Distribution aléatoire
                </h4>
                <p className="text-sm text-amber-700">
                  Les {students.length} étudiant(s) en attente seront distribués
                  équitablement entre les {groups.length} groupe(s) disponibles.
                </p>
              </div>

              {/* Groups Preview */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Groupes disponibles
                </h4>
                <div className="space-y-2">
                  {groups.map((group) => (
                    <div
                      key={group.id}
                      className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm text-gray-900">{group.name}</span>
                      <span className="text-xs text-gray-500">
                        <Users className="h-3 w-3 inline mr-1" />
                        {group.studentCount}
                        {group.maxStudents && `/${group.maxStudents}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
                Environ{' '}
                <strong>
                  {Math.ceil(students.length / groups.length)}
                </strong>{' '}
                étudiant(s) par groupe
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Annuler
          </button>
          {mode === 'manual' ? (
            <button
              onClick={handleManualAssign}
              disabled={isSubmitting || selectedStudents.size === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              Assigner {selectedStudents.size} étudiant(s)
            </button>
          ) : (
            <button
              onClick={handleRandomAssign}
              disabled={isSubmitting || students.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Shuffle className="h-4 w-4" />
              )}
              Distribuer aléatoirement
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignStudentsModal;
